import React from 'react';
import { Mail, Phone, MapPin, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { BRAND } from '../../data/brand';
import { useTranslation } from '../../hooks/useTranslation';

export default function Footer() {
  const { t, currentLanguage } = useTranslation();
  const currentYear = new Date().getFullYear();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  const links = [
    { label: t('footer.privacy') || 'Datenschutz', href: "/datenschutz" },
    { label: t('footer.terms') || 'Impressum', href: "/impressum" },
    { label: t('footer.consent') || 'AGB', href: "/agb" },
  ];

  const socialLinks = [
    { name: "Instagram", href: BRAND.social.instagram, icon: Instagram, color: "hover:text-pink-600" },
    { name: "Facebook", href: BRAND.social.facebook, icon: Facebook, color: "hover:text-blue-600" },
    { name: "WhatsApp", href: BRAND.whatsapp, icon: MessageCircle, color: "hover:text-green-600" },
  ];

  const getSocialButtonStyle = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white';
      case 'facebook':
        return 'bg-blue-600 hover:bg-blue-700 text-white';
      case 'whatsapp':
        return 'bg-green-500 hover:bg-green-600 text-white';
      default:
        return 'bg-gray-600 hover:bg-gray-700 text-white';
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Основной контент футера */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Логотип и описание */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <img 
                src="/images/logo/logo.png" 
                alt={BRAND.name} 
                className="h-28 w-auto"
              />
            </div>
            <p className="text-gray-300 mb-4 max-w-md">
              {t('brand.tagline')}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`p-3 rounded-xl transition-all duration-200 flex items-center justify-center ${getSocialButtonStyle(social.name)}`}
                  aria-label={social.name}
                  title={social.name}
                >
                  <social.icon className="h-5 w-5 text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Контакты */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('contact.title')}</h3>
            <div className="space-y-3 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-rose-600" />
                <a href={`tel:${BRAND.phone}`} className="hover:text-rose-600 transition-colors">
                  {BRAND.phone}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-rose-600" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-rose-600 transition-colors">
                  {BRAND.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-rose-600" />
                <span>{BRAND.address}</span>
              </div>
            </div>
          </div>

          {/* Быстрые ссылки */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.follow_us') || 'Folgen Sie uns'}</h3>
            <div className="space-y-2 text-sm text-gray-300">
              {links.map((link) => (
                <a
                  key={link.label}
                  href={getLocalizedPath(link.href)}
                  className="block hover:text-rose-600 transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Нижняя часть футера */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-sm text-gray-300">
            © {currentYear} {t('brand.name') || 'Alphavita'}. {t('footer.copyright') || 'Alle Rechte vorbehalten'}.
          </div>
          
          <div className="flex items-center gap-6 text-sm text-gray-300">
            <a
              href={getLocalizedPath("/datenschutz")}
              className="hover:text-rose-600 transition-colors font-medium"
            >
              {t('footer.privacy') || 'Datenschutz'}
            </a>
            <a
              href={getLocalizedPath("/impressum")}
              className="hover:text-rose-600 transition-colors font-medium"
            >
              {t('footer.terms') || 'Impressum'}
            </a>
            <a
              href={getLocalizedPath("/contact")}
              className="hover:text-rose-600 transition-colors font-medium"
            >
              {t('hero.cta') || 'Записаться на консультацию'}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
